import React, {useEffect, useRef, useState} from 'react';
import { reservationAttendancesItems} from '../Model/Model';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from "react-i18next";
import TableFunction from "../../../../components/table/TableFunction";
import DropDownMenuProfile from "../../../../components/DropDown/DropDownMenuProfile";
import {
    DeleteIconsOptions,
    DetailsIconsOptions,
    IconMoreVerticalDots, LinkIconsOptions
} from "../../../../components/buttons/IconButtons";
import DropDownMenuItem from "../../../../components/DropDown/DropDownMenuItem";
import {STATUS_MODEL} from "../../../../enums/statusEnum";
import {
    CreateUserInvitationEvent,
    deleteUserInvitationByIds
} from "../../../../reduxToolkit/EmployeeEvents/EmployeeEventsApi";
import {toast} from "react-toastify";
import {
    calculateHeight,
    errorMsg,
    filterByNameEmailPhoneNumber, isSmWidth,
    userDataTransformVisitor
} from "../../../../Apis/functions";
import {useParams} from "react-router-dom";
import {EVENT_INVITATION_MODEL} from "../Manage/Model/Model";
import {permissionObj} from "../../../../Helpers/permission";

/**
 * Hook that manages the logic for the Reservation Attendances module.
 * Handles invitation management (create/delete), tab data updates, search filtering,
 * and actions for each user card (details, delete).
 *
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: FullStack
 * @Email: kpozos@ibl.mx
 *
 * @param {number|string} statusId: Current reservation status ID.
 * @param {function} setModified: Function to update the modified state in the parent component.
 * @param {number|string} selectedTabId: ID of the currently selected tab (user type).
 * @param {boolean} DEV: Enables developer mode for additional options or debugging.
 * @param {Array<string>} permission: List of user permissions.
 */
export const ReservationAttendancesApp = ({ statusId, setModified, selectedTabId, DEV, permission, isOverdue}) => {
    const { id } = useParams();
    const [showUserTypeModal, setShowUserTypeModal] = useState(false);
    const [showVisitorModal, setShowVisitorModal] = useState(false);
    const { groupInvitationsByEventId } = useSelector((state) => state?.EmployeeEventsSlice);
    const [customTabItems, setCustomTabItems] = useState(reservationAttendancesItems)
    const [searchElement, setSearchElement] = useState('');
    const { t } = useTranslation();
    const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState(null);
    const dispatch = useDispatch();
    const { getUserInvitations } = useSelector((state) => state?.EmployeeEventsSlice);
    const { getEventSetting } = useSelector((state) => state?.EmployeeEventsSlice);
    const [userList, setUserList] = useState([]);
    const [invitationLinkModal, setInvitationLinkModal] = useState(false);
    const { eventDetail} = useSelector((state) => state?.EmployeeEventsSlice);
    const attendeesLimit = getEventSetting.attendeesLimitsValue || 0;
    const totalInvitations = groupInvitationsByEventId?.reduce((acc, item) => acc + (item.count || 0), 0) || 0;
    const isLimitReached = getEventSetting?.attendeesLimitsEnabled ? attendeesLimit ? totalInvitations >= attendeesLimit: true : false;
    const attendeesRef = useRef(null);

    useEffect(() => {
        if(isSmWidth()) return;
        calculateHeight({elementRef: attendeesRef, bottom:135})
    }, [results]);

    // Handles table menu logic (open, close, toggle)
    const { handleMenuToggle, handleClose, openMenu } = TableFunction({});

    // Renders dropdown actions for each user card
    const getActionsButtons = (item, index) => {
        return (
            <DropDownMenuProfile
                openMenu={openMenu}
                handleMenuToggle={handleMenuToggle}
                menuId={`reservation-${item.id}`}
                icon={<IconMoreVerticalDots />}
            >
                <DropDownMenuItem
                    index={index}
                    handleClose={handleClose}
                    name={t('details')}
                    icon={<DetailsIconsOptions />}
                    click={() => {
                        setSelected(item?.id);
                        setShowUserDetailsModal(true);
                    }}
                />
                {(eventDetail?.approvalStatusId === STATUS_MODEL.APPROVED && statusId !== STATUS_MODEL.CANCELED) &&
                    !isOverdue &&
                <DropDownMenuItem
                    id={"btn_ita_link_" + item?.id}
                    index={index}
                    handleClose={handleClose}
                    name={t('invitation_link')}
                    click={() => {
                        setSelected(item);
                        setInvitationLinkModal(true);
                    }}
                    icon={<LinkIconsOptions/>}
                />
                }
                { (DEV || permission?.includes(permissionObj?.WEB_EVENT_MANAGE_USERS)) && statusId !== STATUS_MODEL.CANCELED &&
                !isOverdue && (
                    <DropDownMenuItem
                        index={index}
                        handleClose={handleClose}
                        name={t('delete')}
                        fontColor='font-color-error-600'
                        iconColor={"icon-color-error-code-600"}
                        icon={<DeleteIconsOptions />}
                        click={() => {
                            setSelected(item?.id);
                            setShowDeleteModal(true);
                        }}
                    /> )}
            </DropDownMenuProfile>
        );
    };

    /**
     * Deletes a user invitation by ID and updates the list.
     * Displays a success or error message accordingly.
     */
    const deleteUser = () => {
        dispatch(
            deleteUserInvitationByIds({
                id: id,
                body: [selected],
            }),
        ).then(
            ({
                 payload: {
                     data: { success, code },
                 },
             }) => {
                if (success) {
                    setShowDeleteModal(false);
                    toast.success(t('has_been_deleted_successfully'));
                    setModified(true)
                } else {
                    errorMsg(code);
                }
            },
        );
    };

    /**
     * Filters the invitation list based on name, email, or phone number.
     */
    useEffect(() => {
        if(getUserInvitations?.length === 0 || searchElement === "") {
            setResults(getUserInvitations);
            return;
        }
        const filteredOptions = filterByNameEmailPhoneNumber(userDataTransformVisitor(getUserInvitations), searchElement);
        setResults(filteredOptions);
    }, [searchElement, getUserInvitations]);

    /**
     * Updates the custom tab counts based on the grouped invitations by event.
     */
    useEffect(() => {
        let aux = [...customTabItems]
        let updatedTabs = aux.map((tab) => {
            const match = groupInvitationsByEventId.find((e) => e.id === tab.userTypeId);
            return {
                ...tab,
                total: match ? match.count : 0
            };
        });

        setCustomTabItems(updatedTabs);
    }, [groupInvitationsByEventId])

    /**
     * Creates a visitor invitation for the current event.
     * @param {object} visitor - Visitor data used to create the invitation.
     */
    const createVisitorInvitation = (visitor) => {
        if(!visitor){
            errorMsg();
            return;
        }
        const item = {...EVENT_INVITATION_MODEL, eventId: id};
        dispatch(CreateUserInvitationEvent(item.toRegister({
            userList: [{id:visitor.id}],
            userTypeId: selectedTabId,
            visitorType: visitor.visitorTypeId
        }))).then(({payload: {data: { code, success},},}) => {
                    if (success === true) {
                        //toast.success(t("has_been_created_successfully"));
                        setModified(true);
                        setSearchElement("")
                    }else errorMsg(code)
                },
            );
    }

    return {
        DEV,
        permission,
        selected,
        setSelected,
        showUserTypeModal,
        setShowUserTypeModal,
        showVisitorModal,
        setShowVisitorModal,
        customTabItems,
        searchElement,
        setSearchElement,
        showUserDetailsModal,
        setShowUserDetailsModal,
        showDeleteModal,
        setShowDeleteModal,
        results,
        userList, setUserList,
        invitationLinkModal,
        setInvitationLinkModal,
        getActionsButtons,
        deleteUser,
        createVisitorInvitation,
        isLimitReached,
        attendeesLimit,
        attendeesRef
    };
};
